import React, { useState } from "react";
import clsx from "clsx";
import { useHistory } from "react-router-dom";
import { Box, makeStyles, Typography, Dialog, IconButton } from "@material-ui/core";
import { fade } from "@material-ui/core/styles";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { useTranslate } from "react-polyglot";
import coins from "../../assets/ic_coins.png";
import oneCoin from "../../assets/ic_one_coin.png";
import ic_history from "../../assets/ic_history.svg";
import timer from "../../assets/ic_timer.svg";
import shoppingCart from "../../assets/ic_shopping-cart-2.svg";
import icCheck from "../../assets/check-circle.svg";
import { useProductCatalog } from "../../Providers/Data/ProductCatalogProvider.jsx";
import ProductDetails from "./ProductDetails.jsx";
import ProductOrder from "./ProductOrder.jsx";
import { useBaseData } from "../../Providers/Data/BaseDataProvider.jsx";
import OrderSuccessDialog from "./components/OrderSuccessDialog.jsx";
import TransactionsListDialog from "./components/TransactionsListDialog.jsx";
import useDesktopWidthMatch from "../../hooks/useDesktopWidthMatch.js";
import { statusProductOffer } from "../../constants.js";
import { isOneTimeOfferPurchased } from "../../Utils/misc.js";

const useStyles = makeStyles((theme) => ({
  wrapper: {
    margin: "auto",
    padding: "35px 20px",
    [theme.breakpoints.up("lg")]: {
      paddingLeft: 0,
      paddingRight: 0,
      maxWidth: 700,
      width: 700,
      alignSelf: "center",
    },
  },
  mainTitleBox: {
    position: "relative",
  },
  mainTitle: {
    fontSize: 24,
    fontWeight: 500,
    color: "#1B1C1C",
    textAlign: "center",
    padding: "6px 0",
  },
  arrowBack: {
    position: "absolute",
    top: 0,
    left: 0,
  },
  rewardImg: {
    width: 40,
    height: 40,
  },
  cardBox: {
    display: "flex",
    alignItems: "center",
    position: "relative",
    backgroundColor: "#fff",
    border: "2px solid rgba(0, 0, 0, .08)",
    borderRadius: theme.spacing(2),
    padding: "16px 20px",
    cursor: "pointer",
    marginBottom: theme.spacing(1),
  },
  titleBox: {
    margin: "35px 0",
  },
  title: {
    fontSize: 24,
    fontWeight: 500,
    color: "#1B1C1C",
    marginRight: theme.spacing(1.5),
  },
  imageWithIcon: {
    position: "relative",
  },
  imageBox: {
    width: 170,
    height: 170,
    borderRadius: 14,
    overflow: "hidden",
  },
  offerImg: {
    width: "100%",
    height: "100%",
    display: "block",
    objectFit: "cover",
    objectPosition: "center",
  },
  checkIconBox: {
    position: "absolute",
    zIndex: 2,
    top: -7,
    left: -7,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#fff",
    padding: 7,
    "&::before": {
      content: '""',
      position: "absolute",
      inset: 0,
      borderRadius: 20,
      background: fade("#226C36", 0.12),
    },
  },

  checkIcon: {
    width: 26,
    height: 26,
  },
  descriptionBox: {
    height: 170,
    marginLeft: 20,
  },
  titleCard: {
    fontSize: 20,
    fontWeight: 500,
    color: "#1B1C1C",
    height: 30,
    width: 470,
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  mobileTitleCard: {
    height: 60,
    width: 560,
    whiteSpace: "unset",
    display: "-webkit-box",
    "-webkit-line-clamp": 2,
    "-webkit-box-orient": "vertical",
    overflow: "hidden",
  },
  descriptionCard: {
    fontSize: 16,
    color: "#3D4853",
    width: ({ isDesktop }) => (isDesktop ? 470 : 560),
    height: 50,
    display: "-webkit-box",
    "-webkit-line-clamp": 2,
    "-webkit-box-orient": "vertical",
    overflow: "hidden",
  },
  oneCoinImg: {
    width: 24,
    height: 24,
  },
  supportingText: {
    fontSize: 14,
    color: "#54606B",
  },
  buyIconButton: {
    backgroundColor: theme.palette.primary.main,
    padding: "8px 20px",
    borderRadius: 18,
    "&.MuiIconButton-root:hover": {
      backgroundColor: theme.palette.primary.light,
    },
    "&.MuiIconButton-root.Mui-disabled": {
      backgroundColor: "#E2E4E5",
    },
  },
  buyIcon: {
    width: 28,
    height: 28,
  },
  coinBalance: {
    fontSize: 24,
    fontWeight: 500,
    color: theme.palette.secondary.yellow,
    marginRight: theme.spacing(0.5),
  },
  coinPrice: {
    fontSize: 20,
    fontWeight: 500,
    color: theme.palette.secondary.yellow,
    marginRight: theme.spacing(0.5),
  },
  historyIconButton: {
    position: "absolute",
    top: 14,
    right: 8,
  },
}));

/**
 * @returns {*}
 * @constructor
 */
const ProductCatalog = () => {
  const isDesktop = useDesktopWidthMatch();
  const classes = useStyles({ isDesktop: isDesktop });
  const t = useTranslate();
  const history = useHistory();
  const { coinBalance } = useBaseData();
  const { coinOffers, getTransactions } = useProductCatalog();
  const [currentOffer, setCurrentOffer] = useState(false);
  const [step, setStep] = useState(/** @type {?"product"|"order"} */ "");
  const [openOrderSuccess, setOpenOrderSuccess] = useState(false);
  // Источник последней купленной позиции (offers/merch_offers) — нужен для текстов диалога успеха
  const [orderSuccessOfferSource, setOrderSuccessOfferSource] = useState(null);
  const [openTransactionsList, setOpenTransactionsList] = useState(false);

  /** */
  const onClickPayButton = (offer) => {
    setCurrentOffer(offer);
    setStep("product");
  };

  /** */
  const onClosePayDialog = () => {
    setCurrentOffer(null);
  };

  /**
   * Открывает диалог успешного заказа, запоминая источник купленного товара.
   *
   * @param {?string} offerSource - источник купленного товара (offers или merch_offers)
   */
  const showOrderSuccess = (offerSource) => {
    setOrderSuccessOfferSource(offerSource);
    setOpenOrderSuccess(true);
  };

  /** */
  const getCoinTransactions = async () => {
    await getTransactions();
    setOpenTransactionsList(true);
  };

  return (
    <Box className={classes.wrapper}>
      <Box className={classes.mainTitleBox}>
        <IconButton
          className={classes.arrowBack}
          color="inherit"
          onClick={() => {
            history.push("/missions");
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography className={classes.mainTitle}>{t("productCatalog.title")}</Typography>
      </Box>
      <Box className={clsx(classes.cardBox, classes.titleBox)}>
        <Typography className={classes.title}>{t("productCatalog.coinBalance")}</Typography>
        {/* Баланс монет может быть дробным (распределение наград за миссии по предметам), отображаем округлённо */}
        <Typography className={classes.coinBalance}>{Math.round(coinBalance)}</Typography>
        <img className={classes.rewardImg} src={coins} alt="coins" />
        <IconButton className={classes.historyIconButton} color="inherit" onClick={getCoinTransactions}>
          <img className={classes.historyIcon} src={ic_history} alt="historyIcon" />
        </IconButton>
      </Box>
      {coinOffers.length > 0 &&
        coinOffers.map((offer) => (
          <Box key={offer.id} className={classes.cardBox} onClick={() => onClickPayButton(offer)}>
            <Box className={classes.imageWithIcon}>
              <Box className={classes.imageBox}>
                <img className={classes.offerImg} src={offer.image.url} alt={offer.image.name} />
              </Box>
              {offer.status === statusProductOffer.approved && (
                <Box className={classes.checkIconBox}>
                  <img className={classes.checkIcon} src={icCheck} alt="icCheck" />
                </Box>
              )}
            </Box>

            <Box className={classes.descriptionBox}>
              <Typography
                className={clsx(classes.titleCard, {
                  [classes.mobileTitleCard]: !isDesktop,
                })}
              >
                {offer.name}
              </Typography>
              <Typography className={classes.descriptionCard}>
                <span dangerouslySetInnerHTML={offer.description.sanitizedData()} />
              </Typography>
              <Box display="flex" mt={isDesktop ? 6 : 2.5} justifyContent="space-between">
                <Box display="flex">
                  <div className={classes.coinPrice}>{offer.price}</div>
                  <img className={classes.oneCoinImg} src={oneCoin} alt="oneCoin" />
                </Box>
                {isOneTimeOfferPurchased(offer) ? (
                  // Уже купленный одноразовый товар: показываем "Куплено" вместо кнопки покупки
                  <Typography className={classes.supportingText}>{t("productCatalog.purchased")}</Typography>
                ) : coinBalance < offer.price ? (
                  <Typography className={classes.supportingText}>{t("productCatalog.notEnoughCoins")}</Typography>
                ) : (
                  <IconButton
                    disabled={offer.status === statusProductOffer.pending}
                    className={classes.buyIconButton}
                    color="inherit"
                  >
                    {offer.status === statusProductOffer.pending ? (
                      <img className={classes.buyIcon} src={timer} alt="timer" />
                    ) : (
                      <img className={classes.buyIcon} src={shoppingCart} alt="shoppingCart" />
                    )}
                  </IconButton>
                )}
              </Box>
            </Box>
          </Box>
        ))}
      <Dialog open={!!currentOffer} onClose={onClosePayDialog} scroll="body" fullWidth maxWidth="sm">
        {step === "product" && <ProductDetails offer={currentOffer} setStep={setStep} onClose={onClosePayDialog} />}
        {step === "order" && (
          <ProductOrder
            offer={currentOffer}
            setStep={setStep}
            setOpenOrderSuccess={showOrderSuccess}
            onClose={onClosePayDialog}
          />
        )}
      </Dialog>
      <OrderSuccessDialog
        dialogOpen={openOrderSuccess}
        setDialogOpen={setOpenOrderSuccess}
        offerSource={orderSuccessOfferSource}
      />
      <TransactionsListDialog dialogOpen={openTransactionsList} setDialogOpen={setOpenTransactionsList} />
    </Box>
  );
};

export default ProductCatalog;
